/**
 * @file Catalogues squelettes : reliques (12), familiers (6), poissons (6). Entrees = donnees, extensibles sans toucher au moteur.
 */
import type { BiomeId, GradeId } from '@shared/enums';
import { BALANCE } from './balance';
import type { FishDef, ModifierDef, PetDef, RelicDef } from './types';

// ---- Reliques : 3 biomes x (degats, argent, degats critiques) + vitesse, hors-ligne, rebirth ------------------
const BIOME_NAMES: Record<BiomeId, { fr: string; en: string }> = {
  forest: { fr: 'de la foret', en: 'of the forest' },
  desert: { fr: 'du desert', en: 'of the desert' },
  tundra: { fr: 'de la toundra', en: 'of the tundra' },
  autumn: { fr: "de l'automne", en: 'of autumn' },
  blossom: { fr: 'des cerisiers', en: 'of the blossoms' },
};
const BIOMES_LIST: BiomeId[] = ['forest', 'desert', 'tundra'];

const relicFamilies: Array<{ key: string; fr: string; en: string; mod: ModifierDef }> = [
  { key: 'dmg', fr: 'Relique de force', en: 'Power relic', mod: { stat: 'damage', op: 'mult', value: 1.05 } },
  { key: 'money', fr: "Relique d'or", en: 'Gold relic', mod: { stat: 'money', op: 'mult', value: 1.05 } },
  { key: 'crit', fr: 'Relique critique', en: 'Crit relic', mod: { stat: 'critDamage', op: 'mult', value: 1.04 } },
];

export const RELICS: readonly RelicDef[] = [
  ...BIOMES_LIST.flatMap((biome) =>
    relicFamilies.map(
      (f): RelicDef => ({
        id: `relic_${f.key}_${biome}`,
        name: { fr: `${f.fr} ${BIOME_NAMES[biome].fr}`, en: `${f.en} ${BIOME_NAMES[biome].en}` },
        maxLevel: 10,
        perLevel: f.mod,
        rarity: 'uncommon',
        biome,
      }),
    ),
  ),
  { id: 'relic_move', name: { fr: 'Relique de vitesse', en: 'Swift relic' }, maxLevel: 10, perLevel: { stat: 'moveSpeed', op: 'add', value: 0.3 }, rarity: 'rare', biome: 'forest' },
  { id: 'relic_offline', name: { fr: 'Relique du sommeil', en: 'Slumber relic' }, maxLevel: 10, perLevel: { stat: 'offlineHours', op: 'add', value: 0.5 }, rarity: 'rare', biome: 'desert' },
  { id: 'relic_rebirth', name: { fr: 'Relique de renaissance', en: 'Rebirth relic' }, maxLevel: 10, perLevel: { stat: 'rebirthGain', op: 'mult', value: 1.05 }, rarity: 'epic', biome: 'tundra' },
];

// ---- Familiers (19 dans l'original : ici 6 pour illustrer les 3 types) ------------------------------------------
const petCost = (grade: GradeId): PetDef['cost'] => {
  const v = BALANCE.pets.petCoinByGrade[grade];
  return v === undefined ? {} : { petCoin: v };
};
const pet = (id: string, fr: string, en: string, grade: GradeId, petType: PetDef['petType'], bonus: ModifierDef[], skill?: PetDef['skill']): PetDef => ({
  id,
  name: { fr, en },
  grade,
  petType,
  bonus,
  skill,
  cost: petCost(grade),
});

export const PETS: readonly PetDef[] = [
  pet('pet_chick', 'Poussin', 'Chick', 'D', 'buff', [{ stat: 'money', op: 'add', value: 0.1 }]),
  pet('pet_bunny', 'Lapinou', 'Bunny', 'C', 'buff', [{ stat: 'moveSpeed', op: 'add', value: 0.5 }]),
  pet('pet_fox', 'Renardeau', 'Fox kit', 'B', 'singleMissile', [{ stat: 'damage', op: 'add', value: 0.1 }], { cooldownSeconds: 8, power: 5 }),
  pet('pet_owl', 'Hibou', 'Owl', 'A', 'areaLaser', [{ stat: 'critChance', op: 'add', value: 0.02 }], { cooldownSeconds: 12, power: 8 }),
  pet('pet_dragon', 'Dragonneau', 'Drakeling', 'S', 'singleMissile', [{ stat: 'damage', op: 'add', value: 0.25 }], { cooldownSeconds: 10, power: 20 }),
  pet('pet_phoenix', 'Phenix', 'Phoenix', 'SS', 'areaLaser', [{ stat: 'money', op: 'add', value: 0.3 }], { cooldownSeconds: 15, power: 50 }),
];

// ---- Poissons (20 dans l'original, 12 grades : ici 6) ------------------------------------------------------------
const fish = (id: string, fr: string, en: string, grade: GradeId, bonus: ModifierDef[]): FishDef => ({ id, name: { fr, en }, grade, bonus });

export const FISH: readonly FishDef[] = [
  fish('fish_minnow', 'Vairon', 'Minnow', 'D', [{ stat: 'damage', op: 'add', value: 0.02 }]),
  fish('fish_carp', 'Carpe', 'Carp', 'C', [{ stat: 'critChance', op: 'add', value: 0.005 }]),
  fish('fish_trout', 'Truite', 'Trout', 'B', [{ stat: 'damage', op: 'add', value: 0.05 }]),
  fish('fish_koi', 'Koi', 'Koi', 'A', [{ stat: 'critDamage', op: 'add', value: 0.1 }]),
  fish('fish_ray', 'Raie doree', 'Golden ray', 'S', [{ stat: 'damage', op: 'add', value: 0.12 }]),
  fish('fish_leviathan', 'Leviathan', 'Leviathan', 'SS', [{ stat: 'critDamage', op: 'add', value: 0.3 }]),
];
